import React, { useState, useEffect, useMemo } from 'react';
import '../../Styles/Modals/CodeDetectionModal.css';
import { useAttachmentContext } from '../../contexts/AttachmentContext';
import { TextFileRecord } from '../../types/files';
import { suggestFileName } from '../../utils/codeDetection';

interface CodeDetectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  codeContent: string;
  onPasteAnyway: () => void;
}

export const CodeDetectionModal: React.FC<CodeDetectionModalProps> = ({
  isOpen,
  onClose,
  codeContent,
  onPasteAnyway
}) => {
  const { fileName: suggestedFileName, language: detectedLanguage } = useMemo(
    () => suggestFileName(codeContent),
    [codeContent]
  );

  const [fileName, setFileName] = useState(suggestedFileName);
  
  // Get necessary functions from AttachmentContext
  const { setTextFiles } = useAttachmentContext();
  
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
  
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSaveAsAttachment = () => {
    // Make sure we have a valid file name
    const finalFileName = fileName.trim() ? fileName : 'code-snippet.js';
    
    // Create a complete TextFileRecord directly — upload happens at send-time
    const newCodeFile: TextFileRecord = {
      id: `code-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      name: finalFileName,
      size: new Blob([codeContent]).size,
      url: '',
      content: codeContent,
      isLoaded: true,
      type: 'code',
      codeLanguage: detectedLanguage
    };
    
    // Add to textFiles directly
    setTextFiles(prev => [...prev, newCodeFile]);
    
    // Close the modal
    onClose();
  };

  // Show the full code preview (not truncated)
  const codePreview = codeContent;

  return (
    <div 
      className="code-detection-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="code-detection-modal-content">
        <div className="code-detection-modal-header">
          <h1 id="modal-title">Code Detected</h1>
        </div>
        <div className="code-detection-modal-body">
          <p>It looks like you're trying to paste code. Would you like to save it as an attachment instead?</p>
          <div className="code-detection-modal-preview">
            {codePreview}
          </div>
          <input
            className="code-detection-modal-input"
            type="text"
            placeholder="Enter file name"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="code-detection-modal-actions">
          <button 
            className="code-detection-paste-button"
            onClick={() => {
              onPasteAnyway();
              onClose();
            }}
          >
            Paste Anyway
          </button>
          <button 
            className="code-detection-save-button"
            onClick={handleSaveAsAttachment}
          >
            Save as Attachment
          </button>
        </div>
      </div>
    </div>
  );
};

// Add WindowContext integration
interface CodeDetectionState {
  isOpen: boolean;
  codeContent: string;
  onPasteAnyway: () => void;
}

export const useCodeDetectionModal = () => {
  const [codeDetectionState, setCodeDetectionState] = useState<CodeDetectionState>({
    isOpen: false,
    codeContent: '',
    onPasteAnyway: () => {}
  });

  const openCodeDetectionModal = (codeContent: string, onPasteAnyway: () => void) => {
    setCodeDetectionState({
      isOpen: true,
      codeContent,
      onPasteAnyway
    });
  };

  const closeCodeDetectionModal = () => {
    setCodeDetectionState(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  return {
    codeDetectionState,
    openCodeDetectionModal,
    closeCodeDetectionModal
  };
};